
/**
 * This file deals with all the storage related methods that are used by web_apps.
 *
 * To deal with project impurities there is a additional check that is there in getStorage Adapter
 * for Webview. Based on the check we enable or disable encryption for cookies. Please note this
 * while making any changes here.
 *
 * @Owner Web-Core
 *
 * @Scope Storage core methods which will be used by project.
 */
import * as storage from '@groww-tech-pvt/web-storage';
import * as encryptionService from '@groww-tech-pvt/encryption';
import { isEmpty } from '@groww-tech-pvt/ella';

import { identifyBucket } from './helpers';
import { CONSTANTS } from './storageConstants';

//@ts-ignore
let ENCOLD_KEY = ENV_ENCOLD_KEY;

// @ts-ignore
if (__PWA_TARGET__ === 'charts') {

  ENCOLD_KEY = (ENV_ENCOLD_KEY).split('_')[ 0 ];
}

/**
* Checks if webview is active or not
*
* @remarks
* This method is a part of our Storage Adapter.
*
* @internal
*
*/
function getWebViewStatus() {
  const valueFromLocalStorage: any = storage.getDataFromStorage(CONSTANTS.IS_WEBVIEW_ACTIVE, TYPE.LOCAL_STORAGE, identifyBucket(CONSTANTS.IS_WEBVIEW_ACTIVE));

  if (!valueFromLocalStorage) {
    const ans: any = storage.getDataFromStorage(CONSTANTS.IS_WEBVIEW_ACTIVE, TYPE.COOKIE, identifyBucket(CONSTANTS.IS_WEBVIEW_ACTIVE));

    return ans;
  }

  return valueFromLocalStorage;

}

/**
 * Get Encrypted / Unencrypted Data from Storage
 *
 * @remarks
 * This method is a part of our Storage Adapter without any Webview Handling
 *
 * @internal
 *
 */
function getStorageAdapter(storageType : string, key : string, isEncrypted ?: boolean) {
  if (typeof isEncrypted === 'undefined' || isEncrypted == null) {
    isEncrypted = true;
  }

  const storageData = storage.getDataFromStorage(key, storageType, identifyBucket(key));

  if (isEncrypted) {
    const decryptedObject = encryptionService.decryptAes(String(storageData), ENCOLD_KEY);

    return decryptedObject.data;

  } else {
    return storageData;
  }
}

export const TYPE = {
  LOCAL_STORAGE: storage.STORAGE_TYPE_AVAILABLE.LOCAL_STORAGE,
  SESSION_STORAGE: storage.STORAGE_TYPE_AVAILABLE.SESSION_STORAGE,
  COOKIE: storage.STORAGE_TYPE_AVAILABLE.COOKIE,
  LOCAL_COOKIE_STORAGE: storage.STORAGE_TYPE_AVAILABLE.LOCAL_COOKIE_STORAGE
} as const;

/**
* Get Encrypted / Unencrypted Data from Storage
*
* @remarks
* This method is a part of our Storage Adapter with any Webview Handling
*
* @param storageType - The type of storage you want to extract data from.
* @param key - The Key corresponding to which data is to be extracted.
* @param isEncrypted - Whether data will be encrypted or not.
*
* @returns The Data stored in Storage if any, else null.
*
*/

export function getStorage(storageType : string, key : string, isEncrypted ?: boolean) {
  const isWebview = getWebViewStatus();

 // in case of webview we want to have unencrypted cookies and ls as it is.
   // that is why have to add this fallback
  if (isWebview) {
    if (storageType === TYPE.COOKIE) {
      return getStorageAdapter(storageType, key, false);
    }

    if (storageType === TYPE.LOCAL_COOKIE_STORAGE) {
      const lsStoredValue = getStorageAdapter(TYPE.LOCAL_STORAGE, key, isEncrypted);

      if (isEmpty(lsStoredValue)) {
        return getStorageAdapter(TYPE.COOKIE, key, false);

      } else {
        return lsStoredValue;
      }
    }

    return getStorageAdapter(storageType, key, isEncrypted);

  } else {
    return getStorageAdapter(storageType, key, isEncrypted);
  }
}

/**
* Set Encrypted / Unencrypted Data to Storage
*
* @remarks
* This method is a part of our Storage Adapter.
*
* @param storageType - The type of storage you want to set data to.
* @param key - The Key corresponding to which data is to be extracted.
* @param value - The data that is to be stored.
* @param useEncryption - Whether data will be encrypted or not.
* @param expiresInMin - The Expiry time for data in minutes
*
* @returns void
*
*/
export function setStorage(storageType : string, key : string, value : any, useEncryption : boolean, expiresInMin ?: number) {
  if (useEncryption) {
    const encryptedObject = encryptionService.encryptAes(value, ENCOLD_KEY);

    if (encryptedObject.data !== null) {
      storage.setDataToStorage(key, encryptedObject.data, storageType, expiresInMin, identifyBucket(key));

    } else {
      storage.setDataToStorage(key, value, storageType, expiresInMin, identifyBucket(key));
    }

  } else {
    storage.setDataToStorage(key, value, storageType, expiresInMin, identifyBucket(key));
  }
}


function setCrossDomainCookie(key: string, value: string, expiresInMin?: number) {
  if (typeof document === 'undefined') {
    return;
  }

 // Clear cookie from parent domain
  const sameSiteSecure = getSameSiteSecureAttrs();

  document.cookie = `${key}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT${sameSiteSecure}`;

  const parentDomain = getCookieDomain();
  let cookieString = `${key}=${encodeURIComponent(value)}; Path=/`;

  if (parentDomain) {
    cookieString += `; Domain=${parentDomain}`;
  }

  cookieString += getSameSiteSecureAttrs();
  if (expiresInMin) {
    const expiryDate = new Date();

    expiryDate.setTime(expiryDate.getTime() + expiresInMin * 60 * 1000);
    cookieString += `; Expires=${expiryDate.toUTCString()}`;
  }

  document.cookie = cookieString;
}

export function setStorageStrict(storageType : string, key : string, value : any, useEncryption : boolean, expiresInMin ?: number) {
  if (storageType === TYPE.COOKIE || storageType === TYPE.LOCAL_COOKIE_STORAGE) {
    let cookieValue = value;

    if (useEncryption) {
      const encryptedObject = encryptionService.encryptAes(value, ENCOLD_KEY);

      if (encryptedObject.data !== null) {
        cookieValue = encryptedObject.data;
      }
    }

    setCrossDomainCookie(key, String(cookieValue), expiresInMin);

    if (storageType === TYPE.LOCAL_COOKIE_STORAGE) {
      storage.setDataToStorageStrict(key, cookieValue, TYPE.LOCAL_STORAGE, expiresInMin, identifyBucket(key));
    }

    return;
  }

  if (useEncryption) {
    const encryptedObject = encryptionService.encryptAes(value, ENCOLD_KEY);

    if (encryptedObject.data !== null) {
      storage.setDataToStorageStrict(key, encryptedObject.data, storageType, expiresInMin, identifyBucket(key));

    } else {
      storage.setDataToStorageStrict(key, value, storageType, expiresInMin, identifyBucket(key));
    }

  } else {
    storage.setDataToStorageStrict(key, value, storageType, expiresInMin, identifyBucket(key));
  }
}


function isLocalhostOrIP(hostname: string): boolean {
  return hostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname);
}


function getCookieDomain(): string {
 // @ts-ignore - ENV_FLAVOUR is injected at build time
  if (ENV_FLAVOUR === 'local') {
    return '';
  }

  return '.groww.in';
}


function getSameSiteSecureAttrs(): string {
  if (typeof window === 'undefined') {
    return '; SameSite=Lax';
  }

  const isSecure = window.location.protocol === 'https:';

  return isSecure ? '; SameSite=Lax; Secure' : '; SameSite=Lax';
}


function clearCookieWithParentDomain(key: string) {
  if (typeof document === 'undefined') {
    return;
  }

  const parentDomain = getCookieDomain();
  const pastDate = 'Thu, 01 Jan 1970 00:00:01 GMT';
  const sameSiteSecure = getSameSiteSecureAttrs();

  if (parentDomain) {
    document.cookie = `${key}=; Domain=${parentDomain}; Path=/; Expires=${pastDate}${sameSiteSecure}`;
  }

  document.cookie = `${key}=; Path=/; Expires=${pastDate}${sameSiteSecure}`;
  if (typeof window !== 'undefined' && window.location.hostname) {
    const currentHost = window.location.hostname;

    if (!isLocalhostOrIP(currentHost)) {
      document.cookie = `${key}=; Domain=.${currentHost}; Path=/; Expires=${pastDate}${sameSiteSecure}`;
    }
  }
}


function clearAllCookiesWithParentDomain() {
  if (typeof document === 'undefined') {
    return;
  }

  const parentDomain = getCookieDomain();
  const pastDate = 'Thu, 01 Jan 1970 00:00:01 GMT';
  const sameSiteSecure = getSameSiteSecureAttrs();

  const cookies = document.cookie.split(';');

  cookies.forEach(cookie => {
    const key = cookie.split('=')[0].trim();

    if (!key) return;

    if (parentDomain) {
      document.cookie = `${key}=; Domain=${parentDomain}; Path=/; Expires=${pastDate}${sameSiteSecure}`;
    }

    document.cookie = `${key}=; Path=/; Expires=${pastDate}${sameSiteSecure}`;
    if (typeof window !== 'undefined' && window.location.hostname) {
      const currentHost = window.location.hostname;

      if (!isLocalhostOrIP(currentHost)) {
        document.cookie = `${key}=; Domain=.${currentHost}; Path=/; Expires=${pastDate}${sameSiteSecure}`;
      }
    }
  });
}

/**
* Clear a Particular type of storage.
*
* @remarks
* This method is a part of our Storage Adapter.
*
* @param storageType - The type of storage you want to clear
*
* @returns void
*
*/
export function clearStorage(storageType : string, _ignoreKey ?: string) {
  if (storageType === TYPE.COOKIE || storageType === TYPE.LOCAL_COOKIE_STORAGE) {
    clearAllCookiesWithParentDomain();
  }

  storage.clearStorage(storageType);
}

/**
* Clear a Particular key from storage.
*
* @remarks
* This method is a part of our Storage Adapter.
*
* @param storageType - The type of storage you want to clear from.
* @param key - The key which you want to clear.
*
* @returns void
*
*/
export function clearParticularKeyStorage(storageType : string, key : string) {
  if (storageType === TYPE.COOKIE || storageType === TYPE.LOCAL_COOKIE_STORAGE) {
    clearCookieWithParentDomain(key);
  }

  storage.clearKeyFromStorage(key, storageType, identifyBucket(key));
}


function getCookieValue(key: string): string | null {
  if (typeof document === 'undefined') return null;

  const cookies = document.cookie.split(';');

  for (const cookie of cookies) {
    const [ cookieKey, ...cookieValueParts ] = cookie.split('=');
    const trimmedKey = cookieKey?.trim();

    if (trimmedKey === key) {
     // Join back in case the value itself contains '='
      const cookieValue = cookieValueParts.join('=').trim();

      try {
        return decodeURIComponent(cookieValue);

      } catch {
        return cookieValue;
      }
    }
  }

  return null;
}


const CROSS_DOMAIN_COOKIE_MIGRATION_KEY = 'CROSS_DOMAIN_COOKIE_MIGRATED_V2';

// List of critical auth cookies that need to be migrated to cross-domain
const COOKIES_TO_MIGRATE = [
  CONSTANTS.AUTH_SESSION_ID, //'AUTH_SESSION_ID',
  CONSTANTS.AUTHORIZATION, //'lhtndhgfd',
  CONSTANTS.PIN_TOKEN, //'arkeyt473rfh7834cd',
  CONSTANTS.TWO_FACTOR_PIN_ALREADY_SET, //'fsdyuyurewdfi896k7g',
  CONSTANTS.N_KEY_SEED, //'N_KEY_SEED',
  CONSTANTS.SOCKET_TOKEN //'SOCKET_TOKEN',
];

/**
* Migrates existing subdomain-bound cookies to parent domain cookies (.groww.in).
* This ensures that users who logged in before the cross-domain cookie implementation
* can share their session across subdomains.
*
* This function should be called once during app initialization.
* It uses a localStorage flag to ensure it only runs once per browser.
*/
export function migrateExistingCookiesToCrossDomain() {
  if (typeof document === 'undefined' || typeof localStorage === 'undefined') {
    return;
  }

 // Check if migration already done
  try {
    if (localStorage.getItem(CROSS_DOMAIN_COOKIE_MIGRATION_KEY)) {
      return;
    }

  } catch {
   // If localStorage is not available, skip migration
    return;
  }

 // Don't migrate in local environment
 // @ts-ignore - ENV_FLAVOUR is injected at build time
  if (ENV_FLAVOUR === 'local') {
    return;
  }

 // Migrate each cookie
  COOKIES_TO_MIGRATE.forEach(key => {
    const value = getCookieValue(key);

    if (value) {
     // Re-set the cookie with parent domain
      setCrossDomainCookie(key, value);
    }
  });

 // Mark migration as done
  try {
    localStorage.setItem(CROSS_DOMAIN_COOKIE_MIGRATION_KEY, 'true');

  } catch {
   // Ignore localStorage errors
  }
}

export { CONSTANTS } from './storageConstants';

export { errorInStorage } from '@groww-tech-pvt/web-storage';
